import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, EmptyState, Icon, Segmented, Skeleton, Table, TableSkeletonRows, Tooltip, cn, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useBuildFormatters } from "../shared/format";
import type { EvalRun } from "../shared/types";
import { METRICS, configView, formatDelta, overallScore, snapshotToUpdate, totalCases, triggerType } from "./eval-model";
import { useCompare, useEvalMutations, useRunHistory } from "./use-evals";

const COLUMNS = 9;

function triggerLabel(run: EvalRun, t: (key: string, options?: Record<string, unknown>) => string): string {
  const type = triggerType(run);
  return t(`build.evaluations.history.triggers.${type}`, { defaultValue: type });
}

interface RunHistoryProps {
  setId: string;
  setName: string;
  onCompare: (a: EvalRun, b: EvalRun) => void;
}

export function RunHistory({ setId, setName, onCompare }: RunHistoryProps) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const [mode, setMode] = useState<"all" | "config">("all");
  const history = useRunHistory(setId, mode === "config");
  const runs = [...(history.data?.items ?? [])].sort((a, b) => b.number - a.number);
  const latest = runs[0];
  return (
    <section className="card overflow-hidden" aria-labelledby="run-history-title">
      <div className="card-header flex-wrap gap-3">
        <div>
          <h2 id="run-history-title" className="card-title">{t("build.evaluations.history.title")}</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("build.evaluations.history.subtitle", { set: setName })}</p>
        </div>
        <Segmented aria-label={t("build.evaluations.history.title")} value={mode} onValueChange={setMode} options={[{ value: "all", label: t("build.evaluations.history.all") }, { value: "config", label: t("build.evaluations.history.configOnly") }]} />
      </div>
      {history.data && runs.length === 0 ? <EmptyState icon="history" headingLevel={3} title={t("build.evaluations.history.empty")} /> : (
        <Table>
          <thead>
            <tr>
              {(["run", "when", "trigger", "model", "chunk", "reranker", "score", "pass"] as const).map((k) => <th key={k}>{t(`build.evaluations.history.${k}`)}</th>)}
              <th className="text-right"><span className="sr-only">{t("build.evaluations.history.compare")}</span></th>
            </tr>
          </thead>
          <tbody>
            {history.isPending ? <TableSkeletonRows rows={3} columns={COLUMNS} /> : null}
            {runs.map((run, index) => {
              const config = configView(run);
              const score = overallScore(run);
              return (
                <tr key={run.id}>
                  <td className="whitespace-nowrap font-mono text-xs font-semibold">#{run.number}{index === 0 && mode === "all" ? <Badge tone="brand" className="ml-1 font-sans">{t("build.evaluations.history.latest")}</Badge> : null}</td>
                  <td className="whitespace-nowrap text-fg-muted">{fmt.dateTime(run.created_at)}</td>
                  <td className="whitespace-nowrap text-fg-muted">{triggerLabel(run, t)}</td>
                  <td className="whitespace-nowrap font-semibold">{config.model ?? "—"}</td>
                  <td className="tabular-nums">{config.chunkSize === null ? "—" : t("build.evaluations.latest.chunkValue", { value: config.chunkSize })}</td>
                  <td>{config.reranker ? <Badge tone="success">{t("build.evaluations.history.on")}</Badge> : <Badge>{t("build.evaluations.history.off")}</Badge>}</td>
                  <td className="font-bold tabular-nums">{score === null ? "—" : score.toFixed(2)}</td>
                  <td className="tabular-nums text-fg-muted">{run.pass_count}/{totalCases(run)}</td>
                  <td className="text-right">
                    {latest && run.id !== latest.id ? (
                      <Button variant="ghost" size="sm" aria-label={t("build.evaluations.history.compareLabel", { a: run.number, b: latest.number })} onClick={() => { onCompare(run, latest); }}>
                        <Icon name="git-branch" />
                        {t("build.evaluations.history.compare")}
                      </Button>
                    ) : <span className="text-xs text-fg-subtle">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </section>
  );
}

function display(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

interface CompareDialogProps {
  pair: { a: EvalRun; b: EvalRun } | null;
  agentId: string;
  agentName: string;
  canApply: boolean;
  onClose: () => void;
}

/** `compare-modal`: config rows Changed/Same, metric deltas, pass rates, "Use newer config". */
export function CompareDialog({ pair, agentId, agentName, canApply, onClose }: CompareDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const compare = useCompare(pair?.a.id ?? null, pair?.b.id ?? null);
  const { applyConfig } = useEvalMutations(agentId);
  const data = compare.data;
  const newer = pair ? (pair.a.created_at > pair.b.created_at ? pair.a : pair.b) : null;
  const update = newer ? snapshotToUpdate(newer) : {};
  const applicable = Object.keys(update).length > 0;
  const apply = () => {
    if (!newer) return;
    applyConfig.mutate(update, {
      onSuccess: () => { toast.success(t("build.evaluations.compare.applied", { number: newer.number, name: agentName })); onClose(); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  const applyButton = <Button onClick={apply} disabled={!canApply || !applicable} loading={applyConfig.isPending}><Icon name="check" />{t("build.evaluations.compare.useNewer")}</Button>;
  return (
    <Dialog open={pair !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={pair ? t("build.evaluations.compare.title", { a: pair.a.number, b: pair.b.number }) : ""}
        closeLabel={t("common.close")}
        className="max-w-2xl"
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-fg-subtle">{data ? t(data.same_cases ? "build.evaluations.compare.sameCases" : "build.evaluations.compare.differentCases") : null}</span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>{t("build.evaluations.compare.close")}</Button>
              {applicable ? applyButton : <Tooltip content={t("build.evaluations.compare.nothingToApply")}><span>{applyButton}</span></Tooltip>}
            </div>
          </div>
        }
      >
        {compare.isPending ? <Skeleton className="h-64" /> : null}
        {data ? (
          <div className="-mx-5 max-h-[60vh] overflow-y-auto">
            <Table>
              <thead><tr><th>{t("build.evaluations.compare.setting")}</th><th>#{data.a.number}</th><th>#{data.b.number}</th><th className="text-right">{t("build.evaluations.compare.change")}</th></tr></thead>
              <tbody>
                {data.config.map((row) => (
                  <tr key={row.label}>
                    <td className="font-semibold">{row.label}</td>
                    <td className="text-fg-muted">{display(row.a)}</td>
                    <td className={cn(row.state === "Changed" ? "font-bold" : "text-fg-muted")}>{display(row.b)}</td>
                    <td className="text-right">{row.state === "Changed" ? <Badge tone="brand">{t("build.evaluations.compare.changed")}</Badge> : <span className="text-xs text-fg-subtle">{t("build.evaluations.compare.same")}</span>}</td>
                  </tr>
                ))}
                <tr><td colSpan={4} className="bg-surface-2 text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("build.evaluations.compare.scores")}</td></tr>
                {data.metrics.filter((m) => (METRICS as readonly string[]).includes(m.metric)).map((m) => (
                  <tr key={m.metric}>
                    <td className="font-semibold">{t(`build.evaluations.metrics.${m.metric}`, { defaultValue: m.metric })}</td>
                    <td className="text-fg-muted">{m.a?.toFixed(2) ?? "—"}</td>
                    <td>{m.b?.toFixed(2) ?? "—"}</td>
                    <td className="text-right text-xs font-bold">{m.a !== null && m.b !== null ? formatDelta(m.b - m.a, false) : "—"}</td>
                  </tr>
                ))}
                <tr>
                  <td className="font-semibold">{t("build.evaluations.compare.passRate")}</td>
                  <td className="text-fg-muted">{data.a.pass_count}/{totalCases(data.a)}</td>
                  <td>{data.b.pass_count}/{totalCases(data.b)}</td>
                  <td className="text-right text-xs font-bold">{data.b.pass_count - data.a.pass_count >= 0 ? "+" : "−"}{Math.abs(data.b.pass_count - data.a.pass_count)}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
